import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeftIcon, MagnifyingGlassIcon, PlusIcon, TrashIcon, UserIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Combobox } from '@headlessui/react';
import { Button } from '../components/ui/button';
import { Tip } from '../components/ui/tip';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { ChevronDownIcon, Search, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PROVINCIAS_ARGENTINA, CIUDADES_POR_PROVINCIA, TODAS_LAS_CIUDADES } from '../constants/argentinaLocations';
import { confirmDialog } from '../components/ConfirmDialog';
import { toYMD } from '../utils/fechaLocal';
import BillingProfiles from '../components/BillingProfiles';

// Componente DatePicker
const DatePicker = ({ value, onChange, id }: { value?: string; onChange: (date: string) => void; id?: string }) => {
  const [open, setOpen] = useState(false);
  const dateValue = value ? new Date(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          className={cn(
            "w-full justify-start text-left font-normal",
            !dateValue && "text-muted-foreground"
          )}
        >
          {dateValue ? dateValue.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Seleccionar fecha'}
          <ChevronDownIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          captionLayout="dropdown"
          fromYear={1900}
          toYear={new Date().getFullYear()}
          onSelect={(date: Date | undefined) => {
            onChange(date ? toYMD(date) : '');
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

interface Vehicle {
  id?: string;
  make?: string;
  brand?: string;
  model: string;
  licensePlate: string;
  year: string;
  km?: string;
  contactName?: string;
  contactPhone?: string;
}

const NewClient: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const isEdit = Boolean(id);
  const [arcaDocument, setArcaDocument] = useState('');
  const [arcaLookupLoading, setArcaLookupLoading] = useState(false);
  const [vehiclePlateLookupIdx, setVehiclePlateLookupIdx] = useState<number | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleBrands, setVehicleBrands] = useState<string[]>([]);
  const [filteredBrands, setFilteredBrands] = useState<string[]>([]);
  const [showBrandDropdown, setShowBrandDropdown] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const [provinceQuery, setProvinceQuery] = useState('');
  const [cityQuery, setCityQuery] = useState('');

  const { register, handleSubmit, reset, formState: { errors, isValid }, setValue, watch, control } = useForm({
    mode: 'onChange',
    defaultValues: {
      type: 'individual' as 'individual' | 'company',
      firstName: '',
      lastName: '',
      companyName: '',
      dni: '',
      cuit: '',
      email: '',
      phone: '',
      birthDate: '',
      ivaCondition: 'consumidor_final',
      street: '',
      streetNumber: '',
      city: '',
      province: '',
      postalCode: '',
      country: 'Argentina',
      notes: '',
      isFleet: false,
      alsoProvider: false,
      contactPerson: '',
      contactPhone: ''
    }
  });

  const clientType = watch('type');
  const dni = watch('dni');
  const cuit = watch('cuit');
  const selectedProvince = watch('province');
  const isFleet = watch('isFleet');

  const filteredProvinces = useMemo(() => {
    if (!provinceQuery) return PROVINCIAS_ARGENTINA;
    return PROVINCIAS_ARGENTINA.filter(p =>
      p.toLowerCase().includes(provinceQuery.toLowerCase())
    );
  }, [provinceQuery]);

  const availableCities = useMemo(() => {
    if (selectedProvince && CIUDADES_POR_PROVINCIA[selectedProvince]) {
      return CIUDADES_POR_PROVINCIA[selectedProvince];
    }
    return TODAS_LAS_CIUDADES;
  }, [selectedProvince]);

  const filteredCities = useMemo(() => {
    if (!cityQuery) return availableCities;
    return availableCities.filter(c =>
      c.toLowerCase().includes(cityQuery.toLowerCase())
    );
  }, [cityQuery, availableCities]);

  const normalizeArLicensePlate = (value: string) =>
    String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');

  const isValidArLicensePlate = (value: string) => {
    const plate = normalizeArLicensePlate(value);
    return /^[A-Z]{3}\d{3}$/.test(plate) || /^[A-Z]{2}\d{3}[A-Z]{2}$/.test(plate);
  };

  const normalizeVehicleYear = (value: string) => String(value || '').trim();

  const isValidVehicleYear = (value: string) => {
    const yearStr = normalizeVehicleYear(value);
    if (!yearStr) return true;
    const parsed = Number(yearStr);
    const currentYear = new Date().getFullYear();
    return Number.isFinite(parsed) && parsed >= 1900 && parsed <= currentYear;
  };

  useEffect(() => {
    const fetchClient = async () => {
      if (!isEdit || !id) return;
      setLoadingClient(true);
      try {
        const res = await axios.get(`/api/clients/${id}`);
        const client = res.data?.client;
        if (!client) {
          toast.error('Cliente no encontrado');
          navigate('/clients');
          return;
        }

        reset({
          type: client.type || 'individual',
          firstName: client.firstName || '',
          lastName: client.lastName || '',
          companyName: client.companyName || '',
          dni: client.dni || '',
          cuit: client.cuit || '',
          email: client.email || '',
          phone: client.phone || '',
          birthDate: client.birthDate || '',
          ivaCondition: client.ivaCondition || 'consumidor_final',
          street: client.street || '',
          streetNumber: client.streetNumber || '',
          city: client.city || '',
          province: client.province || '',
          postalCode: client.postalCode || '',
          country: client.country || 'Argentina',
          notes: client.notes || '',
          isFleet: Boolean(client.isFleet),
          alsoProvider: Boolean(client.linkedProvider),
          contactPerson: client.contactPerson || '',
          contactPhone: client.contactPhone || ''
        });

        const nextVehicles = Array.isArray(client.vehicles) ? client.vehicles : [];
        setVehicles(
          nextVehicles.map((v: any) => ({
            id: v.id,
            brand: v.make || v.brand || '',
            make: v.make || v.brand || '',
            model: v.model || '',
            licensePlate: v.licensePlate || v.plate || '',
            year: v.year ? String(v.year) : '',
            km: v.mileage ? String(v.mileage) : (v.km ? String(v.km) : ''),
            contactName: v.contactName || '',
            contactPhone: v.contactPhone || ''
          }))
        );
      } catch (error: any) {
        console.error('Error fetching client:', error);
        toast.error(error.response?.data?.error || 'Error al cargar el cliente');
        navigate('/clients');
      } finally {
        setLoadingClient(false);
      }
    };

    void fetchClient();
  }, [id, isEdit, navigate, reset]);

  // Cargar marcas de vehículos
  const fetchVehicleBrands = useCallback(async () => {
    try {
      const response = await axios.get('/api/vehicles/brands');
      const brands = response.data.brands || [];
      setVehicleBrands(brands);
      setFilteredBrands(brands);
    } catch (error) {
      console.error('Error al cargar las marcas de vehículos:', error);
    }
  }, []);

  useEffect(() => {
    fetchVehicleBrands();
  }, [fetchVehicleBrands]);

  const filterBrands = useCallback((searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredBrands(vehicleBrands);
    } else {
      const filtered = vehicleBrands.filter(brand =>
        brand.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBrands(filtered);
    }
  }, [vehicleBrands]);

  // Limpiar y formatear teléfono
  const cleanPhone = (phone: string) => phone.replace(/[^0-9]/g, '').replace(/^549/, '').replace(/^54/, '');

  // Capitalizar texto (primera letra de cada palabra en mayúscula)
  const capitalize = (str: string): string => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Separar calle y número de una dirección
  const parseStreetAddress = (address: string): { street: string; number: string } => {
    if (!address) return { street: '', number: '' };
    // Buscar patrón: texto seguido de número (opcionalmente con bis, piso, etc.)
    const match = address.match(/^(.+?)\s+(\d+.*)$/);
    if (match) {
      return { street: match[1].trim(), number: match[2].trim() };
    }
    return { street: address, number: '' };
  };

  // Detectar tipo de documento automáticamente
  const detectDocumentType = (doc: string): 'DNI' | 'CUIT' | 'CUIL' | null => {
    const cleanDoc = doc.replace(/\D/g, '');
    if (cleanDoc.length === 8) return 'DNI';
    if (cleanDoc.length === 11) {
      // CUIT empieza con 30, 33, 34 (empresas) o 20, 23, 24, 27 (personas)
      const prefix = cleanDoc.substring(0, 2);
      if (['30', '33', '34'].includes(prefix)) return 'CUIT';
      return 'CUIL';
    }
    return null;
  };

  // Buscar en ARCA
  const handleArcaLookup = async () => {
    if (!arcaDocument.trim()) {
      toast.error('Ingresá un DNI/CUIL/CUIT');
      return;
    }

    const cleanDoc = arcaDocument.replace(/\D/g, '');
    const docType = detectDocumentType(cleanDoc);

    if (!docType) {
      toast.error('Documento inválido. Ingresá 8 dígitos para DNI o 11 para CUIT/CUIL');
      return;
    }

    setArcaLookupLoading(true);
    try {
      const response = await axios.post(
        '/api/clients/search-afip',
        { document: cleanDoc },
        { timeout: 20000 }
      );
      const data = response.data?.data || {};

      // Determinar si es empresa o persona física
      if (data.personType === 'JURIDICA' || data.companyName) {
        setValue('type', 'company');
        setValue('companyName', capitalize(data.companyName || ''));
        setValue('cuit', cleanDoc);
        setValue('dni', '');
        setValue('firstName', '');
        setValue('lastName', '');
      } else {
        setValue('type', 'individual');
        setValue('firstName', capitalize(data.firstName || ''));
        setValue('lastName', capitalize(data.lastName || ''));

        if (docType === 'DNI') {
          setValue('dni', cleanDoc);
          setValue('cuit', '');
        } else {
          setValue('cuit', cleanDoc);
          setValue('dni', '');
        }
        setValue('companyName', '');
      }

      // Condición IVA
      if (data.ivaCondition) {
        setValue('ivaCondition', data.ivaCondition);
      }

      // Dirección - separar calle y número si vienen juntos
      if (data.address) {
        let streetValue = data.address.street || '';
        let streetNumberValue = data.address.streetNumber || '';

        // Si no hay número pero la calle contiene número, separarlo
        if (!streetNumberValue && streetValue) {
          const parsed = parseStreetAddress(streetValue);
          streetValue = parsed.street;
          streetNumberValue = parsed.number;
        }

        setValue('street', capitalize(streetValue));
        setValue('streetNumber', streetNumberValue);
        setValue('city', capitalize(data.address.city || ''));
        setValue('province', capitalize(data.address.province || ''));
        setValue('postalCode', data.address.postalCode || '');
      }

      toast.success('Datos cargados desde ARCA');
    } catch (error: any) {
      console.error('Error buscando en ARCA:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('La consulta a ARCA tardó demasiado. Intentá de nuevo.');
      } else {
        toast.error(error.response?.data?.error || 'Error al buscar en ARCA');
      }
    } finally {
      setArcaLookupLoading(false);
    }
  };

  // Al cambiar el documento en el input, detectar tipo automáticamente
  const handleDocumentChange = (value: string) => {
    setArcaDocument(value);
    const cleanDoc = value.replace(/\D/g, '');
    const docType = detectDocumentType(cleanDoc);

    if (docType === 'DNI') {
      setValue('dni', cleanDoc);
      setValue('cuit', '');
    } else if (docType === 'CUIT') {
      setValue('cuit', cleanDoc);
      setValue('dni', '');
      // Si es CUIT de empresa, cambiar tipo automáticamente
      const prefix = cleanDoc.substring(0, 2);
      if (['30', '33', '34'].includes(prefix)) {
        setValue('type', 'company');
      }
    } else if (docType === 'CUIL') {
      setValue('cuit', cleanDoc);
      setValue('dni', '');
    }
  };

  // Funciones para vehículos
  const addVehicle = () => setVehicles([...vehicles, { brand: '', model: '', licensePlate: '', year: '', km: '', contactName: '', contactPhone: '' }]);

  const updateVehicle = (idx: number, field: keyof Vehicle, value: string) => {
    const updated = [...vehicles];
    if (field === 'brand') {
      updated[idx].brand = value;
      updated[idx].make = value;
      filterBrands(value);
      setShowBrandDropdown(idx);
    } else {
      updated[idx] = { ...updated[idx], [field]: value };
    }
    setVehicles(updated);
  };

  const removeVehicle = (idx: number) => setVehicles(vehicles.filter((_, i) => i !== idx));

  // Lookup de vehiculo por patente: completa marca/modelo/anio automaticamente
  const lookupVehiclePlate = async (idx: number) => {
    const rawPlate = vehicles[idx]?.licensePlate || '';
    const plate = normalizeArLicensePlate(rawPlate);
    if (!plate || !isValidArLicensePlate(plate)) {
      toast.error('Patente invalida. Ej: AAA123 o AB123CD');
      return;
    }
    setVehiclePlateLookupIdx(idx);
    try {
      const res = await axios.get(`/api/vehicles/lookup-plate/${plate}`);
      const d = res.data || {};
      const updated = [...vehicles];
      const brandValue = d.brand || d.make || '';
      if (brandValue) {
        updated[idx].brand = brandValue;
        updated[idx].make = brandValue;
      }
      if (d.model) updated[idx].model = d.model;
      if (d.year) updated[idx].year = String(d.year);
      setVehicles(updated);
      toast.success(`Datos cargados: ${brandValue || ''} ${d.model || ''} ${d.year || ''}`.trim() || 'Datos cargados');
    } catch (err: any) {
      if (err?.response?.status === 404) {
        toast.info('Vehículo no encontrado en la base de datos');
      } else {
        toast.error(err?.response?.data?.error || 'Error consultando datos');
      }
    } finally {
      setVehiclePlateLookupIdx(null);
    }
  };

  const selectBrand = (brand: string, idx: number) => {
    const updated = [...vehicles];
    updated[idx].brand = brand;
    updated[idx].make = brand;
    setVehicles(updated);
    setShowBrandDropdown(null);
    setFilteredBrands(vehicleBrands);
  };

  // Submit del formulario
  const onSubmit = async (data: any) => {
    data.phone = cleanPhone(data.phone || '');

    // Validar patentes (formato argentino) antes de enviar
    const normalizedVehicles = vehicles.map((v) => ({
      ...v,
      licensePlate: normalizeArLicensePlate(v.licensePlate),
      year: normalizeVehicleYear(v.year)
    }));
    const invalidVehicle = normalizedVehicles.find(
      (v) => v.licensePlate && !isValidArLicensePlate(v.licensePlate)
    );
    if (invalidVehicle) {
      toast.error('Formato de patente inválido. Ej: AAA123 o AB123CD');
      return;
    }
    const invalidYearVehicle = normalizedVehicles.find((v) => v.year && !isValidVehicleYear(v.year));
    if (invalidYearVehicle) {
      toast.error(`Año inválido. No puede ser mayor al actual (${new Date().getFullYear()}).`);
      return;
    }

    // Si es empresa, usar razón social para nombre y apellido
    if (data.type === 'company' && data.companyName) {
      const parts = data.companyName.trim().split(' ');
      if (parts.length > 1) {
        data.firstName = parts[0];
        data.lastName = parts.slice(1).join(' ');
      } else {
        data.firstName = data.companyName;
        data.lastName = '';
      }
    }

    // Aviso de posible duplicado solo en el alta (best-effort)
    if (!isEdit) {
      try {
        const dup = await axios.post('/api/clients/check-duplicate', {
          phone: data.phone, dni: data.dni, cuit: data.cuit,
          firstName: data.firstName, lastName: data.lastName, companyName: data.companyName
        });
        const matches = dup.data?.matches || [];
        if (matches.length > 0) {
          const lista = matches.slice(0, 3).map((m: any) => `${m.name} (${m.detalle})`).join(' · ');
          const ok = await confirmDialog({
            title: 'Posible cliente duplicado',
            description: `Ya existe: ${lista}. ¿Crear igual?`,
            confirmText: 'Crear igual',
            destructive: true
          });
          if (!ok) return;
        }
      } catch (_) { /* seguir con el alta */ }
    }

    setSubmitting(true);
    try {
      let createdClientId: string | null = null;
      if (isEdit && id) {
        await axios.put(`/api/clients/${id}`, { ...data, vehicles: normalizedVehicles });
        toast.success('Cliente actualizado exitosamente');
        createdClientId = id;
      } else {
        const res = await axios.post('/api/clients', { ...data, vehicles: normalizedVehicles });
        const created = res.data?.client || res.data;
        createdClientId = created?.id || null;
        toast.success('Cliente creado exitosamente');
      }

      if (returnTo && createdClientId) {
        const sep = returnTo.includes('?') ? '&' : '?';
        navigate(`${returnTo}${sep}newClientId=${encodeURIComponent(createdClientId)}`);
      } else {
        navigate('/clients');
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          (isEdit ? 'Error al actualizar el cliente' : 'Error al crear el cliente')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(returnTo || '/clients')}
          className="rounded-full"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{isEdit ? 'Editar Cliente' : 'Nuevo Cliente'}</h1>
          <p className="text-muted-foreground text-sm">
            {isEdit ? 'Actualizá los datos del cliente' : 'Completá los datos del cliente'}
          </p>
        </div>
      </div>

      {loadingClient ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Cargando cliente...
          </CardContent>
        </Card>
      ) : (

      <form onSubmit={handleSubmit(onSubmit, (formErrors) => {
          const errorFields = Object.keys(formErrors).join(', ');
          toast.error(`Datos inválidos en: ${errorFields}`);
        })} className="space-y-6">
        {/* Búsqueda ARCA */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MagnifyingGlassIcon className="h-5 w-5" />
              Buscar en ARCA
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                placeholder="Ingresá DNI (8 dígitos), CUIL o CUIT (11 dígitos)"
                value={arcaDocument}
                onChange={(e) => handleDocumentChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleArcaLookup())}
                className="flex-1"
              />
              <Button
                type="button"
                onClick={handleArcaLookup}
                disabled={arcaLookupLoading}
                variant="secondary"
              >
                {arcaLookupLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Buscando...
                  </span>
                ) : (
                  <>
                    <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                    Buscar
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Ingresá el documento y el sistema detectará automáticamente si es DNI, CUIL o CUIT
            </p>
          </CardContent>
        </Card>

        {/* Tipo de Cliente */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tipo de Cliente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <label className={cn(
                "flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all",
                clientType === 'individual'
                  ? "border-black dark:border-white bg-black dark:bg-white text-white dark:text-black"
                  : "border-muted hover:border-muted-foreground/30"
              )}>
                <input
                  type="radio"
                  {...register('type')}
                  value="individual"
                  className="sr-only"
                />
                <UserIcon className={cn("h-6 w-6", clientType === 'individual' ? "text-white dark:text-black" : "text-muted-foreground")} />
                <div>
                  <p className="font-medium">Persona Física</p>
                  <p className={cn("text-xs", clientType === 'individual' ? "text-white/70 dark:text-black/70" : "text-muted-foreground")}>Cliente particular</p>
                </div>
              </label>
              <label className={cn(
                "flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all",
                clientType === 'company'
                  ? "border-black dark:border-white bg-black dark:bg-white text-white dark:text-black"
                  : "border-muted hover:border-muted-foreground/30"
              )}>
                <input
                  type="radio"
                  {...register('type')}
                  value="company"
                  className="sr-only"
                />
                <BuildingOfficeIcon className={cn("h-6 w-6", clientType === 'company' ? "text-white dark:text-black" : "text-muted-foreground")} />
                <div>
                  <p className="font-medium">Persona Jurídica</p>
                  <p className={cn("text-xs", clientType === 'company' ? "text-white/70 dark:text-black/70" : "text-muted-foreground")}>Empresa u organización</p>
                </div>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Datos Personales / Empresa */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {clientType === 'company' ? 'Datos de la Empresa' : 'Datos Personales'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {clientType === 'company' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="companyName">Razón Social *</Label>
                  <Input
                    id="companyName"
                    {...register('companyName', { required: clientType === 'company' })}
                    placeholder="Nombre de la empresa"
                  />
                  {errors.companyName && (
                    <p className="text-xs text-destructive">La razón social es requerida</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cuit">CUIT *</Label>
                    <Input
                      id="cuit"
                      {...register('cuit', { required: clientType === 'company' })}
                      placeholder="30-12345678-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ivaCondition">Condición IVA</Label>
                    <Controller
                      name="ivaCondition"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="responsable_inscripto">Responsable Inscripto</SelectItem>
                            <SelectItem value="monotributista">Monotributista</SelectItem>
                            <SelectItem value="exento">Exento</SelectItem>
                            <SelectItem value="consumidor_final">Consumidor Final</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contactPerson">Persona de Contacto</Label>
                    <Input
                      id="contactPerson"
                      {...register('contactPerson')}
                      placeholder="Nombre del contacto"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactPhone">Teléfono de Contacto</Label>
                    <Input
                      id="contactPhone"
                      {...register('contactPhone')}
                      placeholder="11 1234-5678"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isFleet"
                    {...register('isFleet')}
                    className="rounded"
                  />
                  <Label htmlFor="isFleet" className="font-normal cursor-pointer">
                    Es una flota de vehículos
                  </Label>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Nombre *</Label>
                    <Input
                      id="firstName"
                      {...register('firstName', { required: clientType === 'individual' })}
                      placeholder="Juan"
                    />
                    {errors.firstName && (
                      <p className="text-xs text-destructive">El nombre es requerido</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Apellido *</Label>
                    <Input
                      id="lastName"
                      {...register('lastName', { required: clientType === 'individual' })}
                      placeholder="Pérez"
                    />
                    {errors.lastName && (
                      <p className="text-xs text-destructive">El apellido es requerido</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="dni">{cuit ? 'CUIL' : 'DNI'}</Label>
                    <Input
                      id="dni"
                      value={cuit || dni}
                      onChange={(e) => {
                        const cleanDoc = e.target.value.replace(/\D/g, '');
                        if (cleanDoc.length <= 8) {
                          setValue('dni', cleanDoc);
                          setValue('cuit', '');
                        } else {
                          setValue('cuit', cleanDoc);
                          setValue('dni', '');
                        }
                      }}
                      placeholder="12345678"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="birthDate">Fecha de Nacimiento</Label>
                    <Controller
                      name="birthDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          id="birthDate"
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ivaCondition">Condición IVA</Label>
                  <Controller
                    name="ivaCondition"
                    control={control}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="consumidor_final">Consumidor Final</SelectItem>
                          <SelectItem value="monotributista">Monotributista</SelectItem>
                          <SelectItem value="responsable_inscripto">Responsable Inscripto</SelectItem>
                          <SelectItem value="exento">Exento</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Datos de facturación alternativos. Solo al editar: hace falta que el
            cliente ya exista para poder colgarle los perfiles. */}
        {isEdit && id && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Facturación</CardTitle>
            </CardHeader>
            <CardContent>
              <BillingProfiles clientId={id} />
            </CardContent>
          </Card>
        )}

        {/* Contacto */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contacto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  {...register('phone')}
                  placeholder="11 1234-5678"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="ejemplo@email.com"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dirección */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dirección</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="street">Calle</Label>
                <Input
                  id="street"
                  {...register('street')}
                  placeholder="Av. Corrientes"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="streetNumber">Número</Label>
                <Input
                  id="streetNumber"
                  {...register('streetNumber')}
                  placeholder="1234"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Provincia</Label>
                <Controller
                  name="province"
                  control={control}
                  render={({ field }) => (
                    <Combobox value={field.value} onChange={(val: string) => {
                      field.onChange(val);
                      // Si cambia la provincia, limpiar ciudad si no pertenece a la nueva provincia
                      const currentCity = watch('city');
                      if (val && CIUDADES_POR_PROVINCIA[val] && currentCity && !CIUDADES_POR_PROVINCIA[val].includes(currentCity)) {
                        setValue('city', '');
                      }
                    }}>
                      <div className="relative">
                        <Combobox.Input
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          placeholder="Seleccionar provincia..."
                          displayValue={(val: string) => val || ''}
                          onChange={(e) => setProvinceQuery(e.target.value)}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                        </Combobox.Button>
                        <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                          {filteredProvinces.length === 0 && provinceQuery !== '' ? (
                            <div className="px-3 py-2 text-sm text-muted-foreground">Sin resultados</div>
                          ) : (
                            filteredProvinces.map((prov) => (
                              <Combobox.Option key={prov} value={prov} as={Fragment}>
                                {({ active, selected }) => (
                                  <li className={cn("cursor-pointer select-none px-3 py-2 text-sm", active && "bg-accent text-accent-foreground")}>
                                    {prov}
                                  </li>
                                )}
                              </Combobox.Option>
                            ))
                          )}
                        </Combobox.Options>
                      </div>
                    </Combobox>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label>Ciudad</Label>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <Combobox value={field.value} onChange={field.onChange}>
                      <div className="relative">
                        <Combobox.Input
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          placeholder="Seleccionar ciudad..."
                          displayValue={(val: string) => val || ''}
                          onChange={(e) => setCityQuery(e.target.value)}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                        </Combobox.Button>
                        <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                          {filteredCities.length === 0 && cityQuery !== '' ? (
                            <div className="px-3 py-2 text-sm text-muted-foreground">Sin resultados</div>
                          ) : (
                            filteredCities.map((city) => (
                              <Combobox.Option key={city} value={city} as={Fragment}>
                                {({ active, selected }) => (
                                  <li className={cn("cursor-pointer select-none px-3 py-2 text-sm", active && "bg-accent text-accent-foreground")}>
                                    {city}
                                  </li>
                                )}
                              </Combobox.Option>
                            ))
                          )}
                        </Combobox.Options>
                      </div>
                    </Combobox>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postalCode">Código Postal</Label>
                <Input
                  id="postalCode"
                  {...register('postalCode')}
                  placeholder="1000"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehículos */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Vehículos</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addVehicle}>
              <PlusIcon className="h-4 w-4 mr-2" />
              Agregar Vehículo
            </Button>
          </CardHeader>
          <CardContent>
            {vehicles.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No hay vehículos agregados
              </p>
            ) : (
              <div className="space-y-4">
                {vehicles.map((vehicle, idx) => (
                  <div key={idx} className="flex gap-3 items-start p-4 bg-muted/30 rounded-lg">
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-12 gap-3">
                      <div className="relative md:col-span-4">
                        <Label className="text-xs">Marca</Label>
                        <Input
                          value={vehicle.brand || ''}
                          onChange={(e) => updateVehicle(idx, 'brand', e.target.value)}
                          onFocus={() => setShowBrandDropdown(idx)}
                          onBlur={() => setTimeout(() => setShowBrandDropdown(null), 200)}
                          placeholder="Ford"
                        />
                        {showBrandDropdown === idx && filteredBrands.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-40 overflow-y-auto">
                            {filteredBrands.slice(0, 10).map((brand) => (
                              <button
                                key={brand}
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  selectBrand(brand, idx);
                                }}
                              >
                                {brand}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="md:col-span-8">
                        <Label className="text-xs">Modelo</Label>
                        <Input
                          value={vehicle.model}
                          onChange={(e) => updateVehicle(idx, 'model', e.target.value)}
                          placeholder="Focus"
                        />
                      </div>
                      <div className="md:col-span-5">
                        <Label className="text-xs">Patente</Label>
                        <div className="flex gap-1">
                          <Input
                            value={vehicle.licensePlate}
                            onChange={(e) => updateVehicle(idx, 'licensePlate', e.target.value.toUpperCase())}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                lookupVehiclePlate(idx);
                              }
                            }}
                            placeholder="ABC123"
                          />
                          <Tip label="Buscar datos por patente">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={vehiclePlateLookupIdx === idx || !vehicle.licensePlate}
                              onClick={() => lookupVehiclePlate(idx)}
                            >
                              {vehiclePlateLookupIdx === idx ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Search className="h-4 w-4" />
                              )}
                            </Button>
                          </Tip>
                        </div>
                      </div>
                      <div className="md:col-span-3">
                        <Label className="text-xs">Año</Label>
                        <Input
                          value={vehicle.year}
                          onChange={(e) => updateVehicle(idx, 'year', e.target.value)}
                          placeholder="2020"
                        />
                      </div>
                      <div className="md:col-span-4">
                        <Label className="text-xs">KM</Label>
                        <Input
                          value={vehicle.km || ''}
                          onChange={(e) => updateVehicle(idx, 'km', e.target.value)}
                          placeholder="50000"
                        />
                      </div>
                      {isFleet && (
                        <>
                          <div className="md:col-span-3">
                            <Label className="text-xs">
                              Contacto del vehículo <span className="text-muted-foreground">(opcional)</span>
                            </Label>
                            <Input
                              value={vehicle.contactName || ''}
                              onChange={(e) => updateVehicle(idx, 'contactName', e.target.value)}
                              placeholder="Nombre del chofer / responsable"
                            />
                          </div>
                          <div className="md:col-span-3">
                            <Label className="text-xs">
                              Teléfono del contacto <span className="text-muted-foreground">(opcional)</span>
                            </Label>
                            <Input
                              value={vehicle.contactPhone || ''}
                              onChange={(e) => updateVehicle(idx, 'contactPhone', e.target.value)}
                              placeholder="3416858698"
                            />
                          </div>
                        </>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeVehicle(idx)}
                      className="text-destructive hover:text-destructive/80 mt-5"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* También es proveedor */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="alsoProvider"
                {...register('alsoProvider')}
                className="rounded"
              />
              <Label htmlFor="alsoProvider" className="font-normal cursor-pointer">
                También es proveedor
              </Label>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Crea (o vincula por CUIT/nombre) la ficha en Proveedores. Las deudas entre las dos
              cuentas corrientes se compensan automáticamente. Desmarcar solo rompe el vínculo, no
              borra la ficha del proveedor.
            </p>
          </CardContent>
        </Card>

        {/* Notas */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notas</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              {...register('notes')}
              placeholder="Observaciones adicionales sobre el cliente..."
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(returnTo || '/clients')}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Guardando...
              </span>
            ) : (
              <>
                {!isEdit && <PlusIcon className="h-4 w-4 mr-2" />}
                {isEdit ? 'Guardar cambios' : 'Crear Cliente'}
              </>
            )}
          </Button>
        </div>
      </form>
      )}
    </div>
  );
};

export default NewClient;
